import React from 'react';
import ResourceLayout from './ResourceLayout';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType, AlignmentType } from 'docx';

const AssessmentRubrics = () => {
  const downloadRubric = async (rubricTitle: string, rubricId: string) => {
    try {
      const rubricElement = document.getElementById(rubricId);
      if (!rubricElement) {
        toast.error('Rubric content not found');
        return;
      }

      const children = [];

      // Add logo and header section for Word document
      children.push(
        new Paragraph({
          text: "EDUGOGY",
          alignment: AlignmentType.LEFT,
          spacing: { after: 200 },
        })
      );

      // Add title
      children.push(
        new Paragraph({
          text: rubricTitle,
          heading: HeadingLevel.TITLE,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        })
      );

      // Add description
      children.push(
        new Paragraph({
          text: 'Professional rubric for evaluating participant performance',
          spacing: { after: 800 },
          alignment: AlignmentType.CENTER,
        })
      );

      // Process the table
      const table = rubricElement.querySelector('table');
      if (table) {
        const tableRows = [];
        const rows = table.querySelectorAll('tr');
        
        rows.forEach((row) => {
          const cells = row.querySelectorAll('th, td');
          const tableCells = [];
          
          cells.forEach((cell) => {
            tableCells.push(
              new TableCell({
                children: [
                  new Paragraph({
                    text: cell.textContent?.trim() || '',
                    alignment: cell.tagName === 'TH' ? AlignmentType.CENTER : AlignmentType.LEFT,
                  })
                ],
                width: { size: 100 / cells.length, type: WidthType.PERCENTAGE },
              })
            );
          });
          
          tableRows.push(
            new TableRow({
              children: tableCells,
            })
          );
        });

        if (tableRows.length > 0) {
          children.push(
            new Table({
              rows: tableRows,
              width: { size: 100, type: WidthType.PERCENTAGE },
            })
          );
        }
      }

      // Add scoring guide if it's included
      const scoringSection = rubricElement.querySelector('.scoring-guide');
      if (scoringSection) {
        children.push(
          new Paragraph({
            text: 'Scoring Guide',
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 600, after: 300 },
          })
        );

        const scoreItems = scoringSection.querySelectorAll('.score-item');
        scoreItems.forEach((item) => {
          const text = item.textContent?.trim();
          if (text) {
            children.push(
              new Paragraph({
                text: text,
                spacing: { after: 100 },
              })
            );
          }
        });
      }

      // Add footer
      children.push(
        new Paragraph({
          text: '© 2024 Edugogy - Professional Development Resources',
          spacing: { before: 800 },
          alignment: AlignmentType.CENTER,
        })
      );

      // Create document
      const doc = new Document({
        sections: [{
          properties: {},
          children: children,
        }],
      });

      // Generate and save
      const blob = await Packer.toBlob(doc);
      const fileName = `${rubricTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.docx`;
      saveAs(blob, fileName);
      toast.success(`${rubricTitle} downloaded successfully!`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download rubric. Please try again.');
    }
  };
  const content = (
    <div className="space-y-8">
      <div className="bg-gradient-card p-6 rounded-lg border" id="participation-rubric">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-primary">Participation Rubric</h2>
          <Button
            onClick={() => downloadRubric('Participation Rubric', 'participation-rubric')}
            size="sm"
            className="flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-muted-foreground/30">
            <thead>
              <tr className="bg-primary/10">
                <th className="border border-muted-foreground/30 p-3 text-left font-semibold">Criteria</th>
                <th className="border border-muted-foreground/30 p-3 text-center font-semibold">Excellent (4)</th>
                <th className="border border-muted-foreground/30 p-3 text-center font-semibold">Proficient (3)</th>
                <th className="border border-muted-foreground/30 p-3 text-center font-semibold">Developing (2)</th>
                <th className="border border-muted-foreground/30 p-3 text-center font-semibold">Beginning (1)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-muted-foreground/30 p-3 font-semibold bg-muted/20">Engagement</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Actively participates in all discussions, asks thoughtful questions, and engages peers constructively</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Regularly participates in discussions and contributes meaningful insights</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Occasionally participates in discussions with some prompting</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Rarely participates and requires significant prompting</td>
              </tr>
              <tr>
                <td className="border border-muted-foreground/30 p-3 font-semibold bg-muted/20">Collaboration</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Works exceptionally well with others, supports team goals, and facilitates group success</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Collaborates effectively and contributes to team objectives</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Works adequately with others but may need guidance</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Struggles to work effectively in group settings</td>
              </tr>
              <tr>
                <td className="border border-muted-foreground/30 p-3 font-semibold bg-muted/20">Preparation</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Always comes prepared with materials, completed pre-work, and relevant questions</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Usually prepared with necessary materials and completed assignments</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Sometimes prepared but may be missing materials or assignments</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Frequently unprepared or missing required materials</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-gradient-card p-6 rounded-lg border" id="project-rubric">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-primary">Project Assessment Rubric</h2>
          <Button
            onClick={() => downloadRubric('Project Assessment Rubric', 'project-rubric')}
            size="sm"
            className="flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-muted-foreground/30">
            <thead>
              <tr className="bg-primary/10">
                <th className="border border-muted-foreground/30 p-3 text-left font-semibold">Criteria</th>
                <th className="border border-muted-foreground/30 p-3 text-center font-semibold">Excellent (4)</th>
                <th className="border border-muted-foreground/30 p-3 text-center font-semibold">Proficient (3)</th>
                <th className="border border-muted-foreground/30 p-3 text-center font-semibold">Developing (2)</th>
                <th className="border border-muted-foreground/30 p-3 text-center font-semibold">Beginning (1)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-muted-foreground/30 p-3 font-semibold bg-muted/20">Content Knowledge</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Demonstrates deep understanding and applies concepts innovatively</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Shows solid understanding and applies concepts correctly</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Shows basic understanding with some gaps in application</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Limited understanding with significant gaps</td>
              </tr>
              <tr>
                <td className="border border-muted-foreground/30 p-3 font-semibold bg-muted/20">Organization</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Exceptionally well-organized with logical flow and clear structure</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Well-organized with clear structure and good flow</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Generally organized but may lack clarity in some areas</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Poor organization makes content difficult to follow</td>
              </tr>
              <tr>
                <td className="border border-muted-foreground/30 p-3 font-semibold bg-muted/20">Innovation</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Highly creative and innovative approach with original thinking</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Shows creativity and some original thinking</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Some evidence of creative thinking but mostly conventional</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Little to no evidence of creative or innovative thinking</td>
              </tr>
              <tr>
                <td className="border border-muted-foreground/30 p-3 font-semibold bg-muted/20">Presentation</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Professional presentation with excellent visual design and error-free content</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Good presentation with minor errors and adequate visual design</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Acceptable presentation but may have errors or poor visual design</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Poor presentation with multiple errors and unprofessional appearance</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-gradient-card p-6 rounded-lg border" id="reflection-rubric">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-primary">Reflection Assessment Rubric</h2>
          <Button
            onClick={() => downloadRubric('Reflection Assessment Rubric', 'reflection-rubric')}
            size="sm"
            className="flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-muted-foreground/30">
            <thead>
              <tr className="bg-primary/10">
                <th className="border border-muted-foreground/30 p-3 text-left font-semibold">Criteria</th>
                <th className="border border-muted-foreground/30 p-3 text-center font-semibold">Excellent (4)</th>
                <th className="border border-muted-foreground/30 p-3 text-center font-semibold">Proficient (3)</th>
                <th className="border border-muted-foreground/30 p-3 text-center font-semibold">Developing (2)</th>
                <th className="border border-muted-foreground/30 p-3 text-center font-semibold">Beginning (1)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-muted-foreground/30 p-3 font-semibold bg-muted/20">Self-Awareness</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Demonstrates deep self-awareness and insight into learning process</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Shows good self-awareness and understanding of learning</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Some evidence of self-awareness but may be superficial</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Little evidence of self-awareness or reflection</td>
              </tr>
              <tr>
                <td className="border border-muted-foreground/30 p-3 font-semibold bg-muted/20">Connection to Practice</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Makes clear, meaningful connections between learning and practice</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Makes good connections between learning and practice</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Makes some connections but may be unclear or weak</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Few or no connections made to practice</td>
              </tr>
              <tr>
                <td className="border border-muted-foreground/30 p-3 font-semibold bg-muted/20">Future Application</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Provides specific, actionable plans for applying learning</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Provides good plans for applying learning</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Provides some plans but may lack specificity</td>
                <td className="border border-muted-foreground/30 p-3 text-sm">Vague or no plans for future application</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-gradient-card p-6 rounded-lg border" id="scoring-guide">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-primary">Scoring Guide</h2>
          <Button
            onClick={() => downloadRubric('Scoring Guide', 'scoring-guide')}
            size="sm"
            className="flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 scoring-guide">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary">Grade Conversion</h3>
            <div className="space-y-2">
              <div className="flex justify-between p-2 rounded bg-green-100 dark:bg-green-900/20 score-item">
                <span className="font-semibold">Excellent (4 points)</span>
                <span>90-100%</span>
              </div>
              <div className="flex justify-between p-2 rounded bg-blue-100 dark:bg-blue-900/20 score-item">
                <span className="font-semibold">Proficient (3 points)</span>
                <span>80-89%</span>
              </div>
              <div className="flex justify-between p-2 rounded bg-yellow-100 dark:bg-yellow-900/20 score-item">
                <span className="font-semibold">Developing (2 points)</span>
                <span>70-79%</span>
              </div>
              <div className="flex justify-between p-2 rounded bg-red-100 dark:bg-red-900/20 score-item">
                <span className="font-semibold">Beginning (1 point)</span>
                <span>Below 70%</span>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary">Assessment Notes</h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start space-x-2">
                <span className="text-primary font-bold">•</span>
                <span>Provide specific feedback for each criterion</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-primary font-bold">•</span>
                <span>Include strengths and areas for improvement</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-primary font-bold">•</span>
                <span>Offer suggestions for continued growth</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-primary font-bold">•</span>
                <span>Connect feedback to learning objectives</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <ResourceLayout
      title="Assessment Rubrics"
      description="Professional rubrics for evaluating participant engagement, projects, and reflections"
      content={content}
      hideMainDownload={true}
    />
  );
};

export default AssessmentRubrics;